import {
  EXTENSION_MIMETYPE,
  Flex,
  isUserWithRoles,
  useCombinedUser,
  useDownloadFile,
  useIsMsalAuth,
  UserRoles,
  useStyledTheme,
} from '@equifinance/shared';
import styled from 'styled-components';
import moment from 'moment';
import useDocumentsApi from '../../../../api/useDocumentsApi';
import { DocumentaryEvidenceStatus } from './DocumentEvidenceStatus';
import { DocumentEvidence, DocumentStatus, useTasksContext } from '../../../../contexts/tasks';
import { useAccountContext } from '../../../../contexts';

const DocumentaryEvidenceItemContainer = styled(Flex) <{ borderColor?: string }>`${({ theme, borderColor }) => `
  justify-content: space-between;
  align-items: center;
  border-left: 12px solid ${borderColor};
  border-radius: ${theme.borderRadius[2]};
  background: ${theme.colours.neutral[9]};
`}`;

const DocumentNameContainer = styled.div`${({ theme }) => `
  padding: 0 ${theme.padding[3]};
`}`;

const DocumentName = styled.div`${({ theme }) => `
  width: ${theme.taskEvidenceDocNameWidth};
  padding: ${theme.padding[2]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const DocumentTimeContainer = styled.div`${({ theme }) => `
  width: ${theme.taskEvidenceDatetimeWidth};
  padding: ${theme.padding[2]}; ${theme.padding[3]};
  border-left: 1px solid ${theme.colours.neutral[7]};
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.neutral[2]};
`}`;

const DocumentTimeLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
`}`;

const DocumentTimeValue = styled.span``;

const DocumentAction = styled(Flex)`${({ theme }) => `
  justify-content: flex-end;
  align-items: center;
  padding: 0 ${theme.padding[3]};
  width: ${theme.taskEvidenceActionsWidth};
`}`;

const DownloadIcon = styled.i`${({ theme }) => `
  padding-right: ${theme.padding[1]};
  font-size: ${theme.fontSize[3]};
  cursor: pointer;
`}`;

export const HiddedA = styled.a`display:hidden;`;

function useBorderColor(status: keyof typeof DocumentStatus): string | undefined {
  const { colours } = useStyledTheme();

  switch (status) {
    case 'Approved': return colours.success[1];
    case 'ApprovedSubjectToExpiry': return colours.success[1];
    case 'Uploaded': return colours.info[1];
    case 'Deleted': return colours.neutral[7];
    case 'SubmittedToLender': return colours.highlight[1];
    case 'WithThirdParty': return colours.info[1];
    case 'Rejected': return colours.danger[1];
    case 'Expired': return colours.danger[1];
    case 'ToExpire': return colours.secondary[1];
    case 'UnderwriterDocument': return colours.warning[3];
    default: return colours.neutral[8];
  }
}

export function DocumentaryEvidenceItem({ document, taskIndex, documentIndex }: { document: DocumentEvidence, taskIndex: number, documentIndex: number }) {
  const { id: accountId } = useAccountContext();
  const { updateTasks } = useTasksContext();
  const { status, fileName, uploadedDate } = document;
  const borderColor = useBorderColor(status);
  const { getDocument } = useDocumentsApi();
  const isInternalUser = useIsMsalAuth();
  const { user } = useCombinedUser();
  const isUnderwriter = isInternalUser && isUserWithRoles([UserRoles.UNDERWRITER], user);
  const readOnlyStatus = !isUnderwriter || document.status === DocumentStatus.Deleted;

  const onSelect = (status: string) => {
    updateTasks(`tasks[${taskIndex}].documentEvidence[${documentIndex}].status`, status);
  };

  const {
    downloadFile,
    downloadFileRef,
  } = useDownloadFile();

  const downloadDocument = async () => {
    const { documentData, fileName, documentType } = await getDocument(accountId, document.documentId);
    downloadFile(fileName, documentData, EXTENSION_MIMETYPE[documentType]);
  };

  return (
    <DocumentaryEvidenceItemContainer borderColor={borderColor}>
      <Flex>
        <DocumentNameContainer>
          <DocumentName>{fileName}</DocumentName>
        </DocumentNameContainer>
        <DocumentTimeContainer>
          <DocumentTimeLabel>Uploaded:</DocumentTimeLabel>
          <DocumentTimeValue>{` ${moment(uploadedDate).format('DD/MM/YY')}`}</DocumentTimeValue>
        </DocumentTimeContainer>
        <DocumentTimeContainer>
          <DocumentTimeLabel>Expiry date:</DocumentTimeLabel>
          <DocumentTimeValue>{ }</DocumentTimeValue>
        </DocumentTimeContainer>
      </Flex>
      <Flex>
        <DocumentaryEvidenceStatus status={status} onSelect={onSelect} readOnly={readOnlyStatus} />
        <DocumentAction>
          <DownloadIcon className="mi-download" onClick={downloadDocument} />
          <HiddedA ref={downloadFileRef} />
        </DocumentAction>
      </Flex>
    </DocumentaryEvidenceItemContainer>
  );
}
